import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable, InjectionToken, Injector, Provider } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Link, LinkBreezeModel } from "@common/ADAPT.Common.Model/organisation/link";
import { Meeting, MeetingBreezeModel, MeetingStatus } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem, MeetingAgendaItemBreezeModel, MeetingAgendaItemStatus, MeetingAgendaItemType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaItemSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item-supplementary-data";
import { MeetingAgendaTemplate, MeetingAgendaTemplateBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { MeetingAttendee, MeetingAttendeeBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-attendee";
import { MeetingNote, MeetingNoteBreezeModel, MeetingNoteType } from "@common/ADAPT.Common.Model/organisation/meeting-note";
import { MeetingSupplementaryData, MeetingSupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/meeting-supplementary-data";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamMeetingGuidanceBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-meeting-guidance";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { ServiceUri } from "@common/configuration/service-uri";
import { ImplementationKitService } from "@common/implementation-kit/implementation-kit.service";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BreezeService } from "@common/lib/data/breeze.service";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { UserService } from "@common/user/user.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { BaseOrganisationService } from "@org-common/lib/organisation/base-organisation.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import isEqual from "lodash.isequal";
import moment from "moment";
import { defer, EMPTY, forkJoin, lastValueFrom, Observable, of, Subject } from "rxjs";
import { catchError, filter, map, share, switchMap, take, tap } from "rxjs/operators";
import { ItemUtilities } from "../kanban/items/item-utilities";
import { KanbanAuthService } from "../kanban/kanban-auth.service";
import { CommonTeamsService } from "../teams/common-teams.service";
import { CommonTeamsAuthService } from "../teams/common-teams-auth.service";
import { DefaultMeetingFilter } from "./meetings-filter/meetings-filter.component";
import { teamActiveMeetingPageRoute } from "./team-active-meeting-page/team-active-meeting-page.route";

type AdaptRoute = IAdaptRoute<any>;
const TEAM_MEETINGS_PAGE = new InjectionToken<AdaptRoute>("TEAM_MEETINGS_PAGE_TOKEN");

export function provideTeamMeetingsPageRoute(route: AdaptRoute): Provider {
    return {
        provide: TEAM_MEETINGS_PAGE,
        useValue: route,
        multi: false,
    };
}

export enum MeetingAgendaType {
    AdHoc,
    System,
    Organisation,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Team,
}

export interface IMeetingAgendaDetail {
    meetingTemplate: MeetingAgendaTemplate;
    type: MeetingAgendaType;
}

export interface IMeetingFilterParams {
    peopleIds: number[];
    showDecisions?: boolean;
    showMinutes?: boolean;
    showActions?: boolean;
    showObjectives?: boolean;
    fromDate?: Date;
    toDate?: Date;
    // display options
    displayAllAgendaItems?: boolean; // if true, will display all agenda items, including those without the filtered elements
    displayAgendaItemDescriptions?: boolean;
}

const AllMeetingsForPermissionVerificationRequestKey = "allMeetingsForPermissionVerification";

export interface IMeetingTeamGroup {
    team?: Team;
    meetings: Meeting[];
}

@Injectable({
    providedIn: "root",
})
export class MeetingsService extends BaseOrganisationService {
    // this is just to flow event from meeting-tab-content to team-meetings-page
    private readonly _meetingIdChange$ = new Subject<number>();
    private readonly _meetingForMeetingAttendeeChangedForCurrentPerson$: Observable<Meeting | undefined>;

    // items with no code will be first (i.e. user added templates), then sorted by name
    public meetingAgendaTemplateSortFunction = SortUtilities.getSortByFieldFunction<MeetingAgendaTemplate>(
        (t) => !!t.code,
        (t) => t.name);

    // This is used to keep track of the last date range for team meetings query (including meeting notes)
    // - to clear cache if the date range changes
    // - didn't include predicate into the request key as that key is also used as encompassing key for other smaller query
    //   - is a pain if having to form the predicate the encompassing query which covers the current query
    //     e.g. get meeting notes for meetings in team is already covering get meeting notes for meeting - only meetings for team has the date range
    //     - we will not be getting meeting notes for a meeting if the meeting is filtered out by the date range or there won't be encompassing query
    private cacheDateRangeKey: { [teamId: number]: string | undefined } = {};

    public constructor(
        injector: Injector,
        private orgService: OrganisationService,
        private userService: UserService,
        private teamsService: CommonTeamsService,
        private authorisationService: AuthorisationService,
        private featuresService: FeaturesService,
        private httpClient: HttpClient,
        private implementationKitService: ImplementationKitService,
        private kanbanAuthService: KanbanAuthService,
        @Inject(TEAM_MEETINGS_PAGE) private teamMeetingsPageRoute: AdaptRoute,
        rxjsBreezeService: RxjsBreezeService,
        breezeService: BreezeService,
    ) {
        super(injector);
        this._meetingForMeetingAttendeeChangedForCurrentPerson$ = rxjsBreezeService.entityTypeChanged(MeetingAttendee).pipe(
            filter((meetingAttendee) => meetingAttendee.attendeeId === this.userService.getCurrentPersonId()),
            tap((meetingAttendee) => {
                this.commonDataService.clearQueryCacheForRequestKey(AllMeetingsForPermissionVerificationRequestKey);
                this.commonDataService.clearQueryCacheForRequestKey(this.getCurrentActiveMeetingForPersonRequestKey(this.userService.getCurrentPersonId()!));
                this.commonDataService.clearQueryCacheForRequestKey(this.getMeetingByIdRequestKey(meetingAttendee.meetingId));
            }),
            // just in case is an attendee deletion -> meeting won't be defined. Need meeting entity to determine team
            switchMap((meetingAttendee) => meetingAttendee.meeting
                ? of(meetingAttendee.meeting)
                : this.getMeetingById(meetingAttendee.meetingId)),
            tap((meeting) => {
                if (meeting) {
                    this.commonDataService.clearQueryCacheForRequestKey(this.getTeamMeetingsRequestKey(meeting.teamId));
                }
            }),
            share(),
        );

        // clear the cache date range key when switching org - though this is not strictly necessary as
        // this cache will result in clearing the breeze cache when key mismatch - breeze cache is already cleared
        // - this is just to remove the left behind teamId keys from irrelevant orgs
        breezeService.dataCacheCleared$.subscribe(() => this.cacheDateRangeKey = {});
    }

    public static getMeetingAgendaItemsTotalPlannedDurationInMinutes(meetingAgendaItems: MeetingAgendaItem[]) {
        return meetingAgendaItems
            .filter((item) => item.type === MeetingAgendaItemType.AgendaItem)
            .reduce((previous, current) => previous + (current?.plannedDurationInMinutes ?? 0), 0);
    }

    public get meetingForMeetingAttendeeChangedForCurrentPerson$() {
        return this._meetingForMeetingAttendeeChangedForCurrentPerson$;
    }

    public get meetingIdChange$() {
        return this._meetingIdChange$.asObservable();
    }

    public get canAccessFacilitationNote() {
        if (!this.authorisationService.currentPerson) {
            return false;
        }
        return this.authorisationService.currentPerson.getActiveConnections().some((c) => c.isCoachConnection()) || this.authorisationService.currentPerson.isStakeholderManager;
    }

    public notifyMeetingIdChange(meetingId: number) {
        this._meetingIdChange$.next(meetingId);
    }

    public canViewTeamMeetings(teamId: number) {
        return this.authorisationService.getHasAccess(CommonTeamsAuthService.ViewTeamMeetings, { teamId });
    }

    public importAgendaItems(agendaItems: MeetingAgendaItem[], meetingId?: number, meetingAgendaTemplateId?: number, latestOrdinal?: number, hasPreWork = false, hasFacilitatonNotes = false) {
        if (agendaItems.length > 0) {
            return forkJoin(agendaItems.map((item, index) => this.createAgendaItem(
                latestOrdinal
                    ? latestOrdinal + index + 1
                    : item.ordinal,
                meetingId,
                meetingAgendaTemplateId,
            ).pipe(
                switchMap((newItem) => {
                    newItem.name = item.name;
                    newItem.plannedDurationInMinutes = item.plannedDurationInMinutes;
                    newItem.articleSlug = item.articleSlug;
                    newItem.componentSelector = item.componentSelector;

                    if ((hasPreWork && item.type === MeetingAgendaItemType.PreWork)
                        || (hasFacilitatonNotes && item.type === MeetingAgendaItemType.FacilitationNotes)) {
                        newItem.type = MeetingAgendaItemType.AgendaItem;
                    } else {
                        newItem.type = item.type;
                    }

                    if (item.supplementaryData?.itemDescription) {
                        return this.createAgendaItemSupplementaryData(newItem).pipe(
                            map((suppData) => {
                                suppData.itemDescription = item.supplementaryData?.itemDescription ?? "";
                                return newItem;
                            }),
                        );
                    } else {
                        return of(newItem);
                    }
                }),
            ))).pipe(
                switchMap((newItems) => this.fetchArticlesForAgendaItems(newItems)),
                // clear the articleSlugs once the article content has been fetched.
                // otherwise, if this is for a template, any changes made will be overwritten when the template is imported
                // as helpjuice content will be fetched again.
                tap((newItems) => newItems.forEach((item) => item.articleSlug = undefined)),
            );
        } else {
            return of([]);
        }
    }

    // sort by the type enum, then alphabetically
    public sortTemplates(a: IMeetingAgendaDetail, b: IMeetingAgendaDetail) {
        return a.type - b.type || a.meetingTemplate.name.localeCompare(b.meetingTemplate.name);
    }

    public copyMeetingTemplateToTeam(meetingTemplate: MeetingAgendaTemplate, teamId?: number) {
        return this.getAgendaItemsForMeetingAgendaTemplate(meetingTemplate.meetingAgendaTemplateId).pipe( // need to prime this first as I have seen empty copy if not popping the tooltip beforehand
            switchMap(() => this.createAgendaTemplate(teamId)),
            switchMap((newTemplate) => {
                newTemplate.name = `Copy of ${meetingTemplate.name}`;
                newTemplate.meetingDescription = meetingTemplate.meetingDescription;
                // Don't copy code or the copied template cannot be deleted or edited
                // newTemplate.code = meetingTemplate.code;
                newTemplate.meetingDescriptionArticleSlug = meetingTemplate.meetingDescriptionArticleSlug;
                newTemplate.version = meetingTemplate.version;
                return this.commonDataService.saveEntities(newTemplate);
            }),
            switchMap((newTemplate: MeetingAgendaTemplate) =>
                this.importAgendaItems(meetingTemplate.meetingAgendaItems, undefined, newTemplate.meetingAgendaTemplateId).pipe(
                    switchMap(() => this.saveEntities()), // save all - agenda items can have supp data
                    map(() => newTemplate),
                )),
        );
    }

    private fetchArticlesForAgendaItems(agendaItems: MeetingAgendaItem[]) {
        const articleRequests = Object.fromEntries(agendaItems
            .filter((item) => !!item.articleSlug)
            .map((item) => [item.articleSlug!, this.implementationKitService.getArticle(item.articleSlug!).pipe(
                catchError((err) => {
                    this.log.error(`Failed to fetch article ${item.articleSlug}. Error: ${err}`);
                    return of(null);
                }),
            )]),
        );

        if (Object.keys(articleRequests).length === 0) {
            // no article fetched, nothing to change for the agenda items
            return of(agendaItems);
        }

        return forkJoin(articleRequests).pipe(
            switchMap((articles) => {
                return forkJoin(agendaItems.map((item) => {
                    const article = item.articleSlug ? articles[item.articleSlug] : null;
                    if (!article) {
                        return of(undefined);
                    }

                    const suppData$ = item.supplementaryData
                        ? of(item.supplementaryData)
                        : this.createAgendaItemSupplementaryData(item);
                    return suppData$.pipe(
                        tap((suppData) => suppData.itemDescription = article.answer),
                    );
                }));
            }),
            map(() => agendaItems),
        );
    }

    public fetchArticlesForMeetingSuppData(meeting: Meeting, articleSlug?: ImplementationKitArticle) {
        if (!articleSlug) {
            return of(meeting);
        }

        return this.implementationKitService.getArticle(articleSlug).pipe(
            switchMap((article) => {
                if (!article) {
                    return of(meeting);
                }
                const suppData$ = meeting.supplementaryData
                    ? of(meeting.supplementaryData)
                    : this.createMeetingSupplementaryData(meeting);
                return suppData$.pipe(
                    tap((suppData) => suppData.purpose = article.answer),
                    map(() => meeting),
                );
            }),
            catchError((err) => {
                this.log.error(`Failed to fetch article ${articleSlug}. Error: ${err}`);
                return of(null);
            }),
        );
    }

    public updateMeetingDurationFromAgendaTemplate(meeting: Meeting, template: MeetingAgendaTemplate) {
        return this.getAgendaItemsForMeetingAgendaTemplate(template.meetingAgendaTemplateId).pipe(
            tap((agendaItems) => this.updateMeetingDurationFromAgendaItems(meeting, agendaItems)),
            map(() => meeting),
            catchError(() => of(meeting)),
        );
    }

    public updateMeetingDurationFromAgendaItems(meeting: Meeting, agendaItems?: MeetingAgendaItem[]) {
        const time = (agendaItems ?? meeting.meetingAgendaItems)
            .filter((item) => item.type !== MeetingAgendaItemType.PreWork && item.type !== MeetingAgendaItemType.FacilitationNotes)
            .reduce((duration, item) => duration + (item.plannedDurationInMinutes ?? 0), 0);

        if (time > 0) {
            meeting.endTime = moment(meeting.meetingDateTime)
                .add(time, "minutes")
                .toDate();
        } else {
            meeting.endTime = moment(meeting.meetingDateTime)
                .add(1, "hour")
                .toDate(); // default to 1 hour if no agenda items
        }

        meeting.extensions.updateSelectedDuration();
    }

    public createMeeting(teamId: number) {
        const now = new Date();
        const initialData: Partial<Meeting> = {
            teamId,
            organisationId: this.orgService.getOrganisationId(),
            meetingDateTime: moment(now).add(1, "h").startOf("h").toDate(), // default to start of next hour
            createdDateTime: now,
            status: MeetingStatus.NotStarted,
        };

        return this.commonDataService.create(MeetingBreezeModel, initialData);
    }

    /**
     * Creates supplementary data for the meeting, and adds the default attendees if required.
     */
    public initialiseMeeting(meeting: Meeting) {
        return defer(async () => {
            meeting.extensions.updateSelectedDuration();

            // create supp data for meeting if it doesn't exist already
            if (!meeting.supplementaryData) {
                const suppData = await lastValueFrom(this.createMeetingSupplementaryData(meeting));
                meeting.supplementaryData = suppData;
            }

            // only create default attendees if there are not already attendees
            if (meeting.entityAspect.entityState.isAdded() && meeting.meetingAttendees.length === 0) {
                await lastValueFrom(this.createDefaultMeetingAttendees(meeting));
            }

            return meeting;
        });
    }

    public rescheduleMeeting(meeting: Meeting) {
        const endTime = moment(meeting.endTime);
        const meetingDuration = endTime.isAfter(meeting.meetingDateTime)
            ? moment.duration(endTime.diff(meeting.meetingDateTime))
            : moment.duration(1, "h");
        meeting.meetingDateTime = moment().add(1, "h").startOf("h").toDate(); // default 1h from now
        meeting.endTime = moment(meeting.meetingDateTime).add(meetingDuration).toDate(); // maintain previous planned duration
        meeting.status = MeetingStatus.NotStarted;
        return meeting;
    }

    public getMeetingCalendarFile(meeting: Meeting) {
        const uri = `${ServiceUri.MethodologyServicesServiceBaseUri}/MeetingCalendarExport`;
        let params = new HttpParams();
        params = params.set("meetingId", meeting.meetingId.toString());

        return this.httpClient.get(
            uri,
            {
                params,
                responseType: "blob", // makes response.body a Blob object
                observe: "response", // without this, response.body will be unknown
            },
        );
    }

    public sendMeetingCalendarInvites(meeting: Meeting) {
        if (meeting.extensions.isEnded) {
            this.log.error("MeetingService::sendMeetingCalendarInvites tried to send an invite, but it was ended");
            return EMPTY;
        }

        const uri = `${ServiceUri.MethodologyServicesServiceBaseUri}/MeetingCalendarSendInvites`;
        let params = new HttpParams();
        params = params.set("meetingId", meeting.meetingId.toString());

        return this.httpClient.post(uri, null, { params, observe: "response" });
    }

    public createMeetingAttendee(meetingId: number, personId: number) {
        const initialData: Partial<MeetingAttendee> = {
            meetingId,
            attendeeId: personId,
        };

        return this.commonDataService.create(MeetingAttendeeBreezeModel, initialData);
    }

    public createDefaultMeetingAttendees(meeting: Meeting) {
        return this.teamsService.getTeamById(meeting.teamId).pipe(
            switchMap((team) => this.teamsService.promiseToGetTeamMemberRoleConnections(team!, true)),
            map((roleConnections) => roleConnections.map((rc) => rc.connection.person)),
            switchMap((people) => people?.length
                ? forkJoin(people.map((p) => this.createMeetingAttendee(meeting.meetingId, p.personId)))
                : of([])),
        );
    }

    public createAgendaItem(ordinal: number, meetingId?: number, meetingAgendaTemplateId?: number, type = MeetingAgendaItemType.AgendaItem) {
        return this.commonDataService.create(MeetingAgendaItemBreezeModel, {
            meetingId,
            meetingAgendaTemplateId,
            ordinal,
            status: MeetingAgendaItemStatus.NotStarted,
            type,
        });
    }

    public createAgendaTemplate(teamId?: number) {
        const initialData: Partial<MeetingAgendaTemplate> = {
            organisationId: this.orgService.getOrganisationId(),
            teamId,
        };

        return this.commonDataService.create(MeetingAgendaTemplateBreezeModel, initialData);
    }

    public createAgendaItemSupplementaryData(meetingAgendaItem: MeetingAgendaItem) {
        // check if supplementary data exists first before creating
        const existingSuppData = meetingAgendaItem.entityAspect.entityState.isAdded()
            ? of(meetingAgendaItem.supplementaryData) // newly added agenda item entity -> if there is already supp data attached, use it
            : this.commonDataService.getById(MeetingAgendaItemSupplementaryDataBreezeModel, meetingAgendaItem.meetingAgendaItemId);

        return existingSuppData.pipe(switchMap((suppData) => suppData
            ? of(suppData)
            : this.commonDataService.create(MeetingAgendaItemSupplementaryDataBreezeModel, { meetingAgendaItem })));
    }

    public createMeetingSupplementaryData(meeting: Meeting) {
        // check if supplementary data exists first before creating
        const existingSuppData = meeting.entityAspect.entityState.isAdded()
            ? of(meeting.supplementaryData) // newly added meeting entity -> if there is already supp data attached, use it
            : this.getSupplementaryDataForMeeting(meeting.meetingId);

        return existingSuppData.pipe(switchMap((suppData) => suppData
            ? of(suppData)
            : this.commonDataService.create(MeetingSupplementaryDataBreezeModel, { meeting })));
    }

    public getMeetingAgendaTemplates() {
        return this.commonDataService.getAll(MeetingAgendaTemplateBreezeModel);
    }

    public getMeetingAgendaTemplateByCode(code: string) {
        const predicate = new MethodologyPredicate<MeetingAgendaTemplate>("code", "==", code);
        return this.commonDataService.getByPredicate(MeetingAgendaTemplateBreezeModel, predicate).pipe(
            map(ArrayUtilities.getSingleFromArray),
        );
    }

    public ascendingMeetingsSortFunction(a: Meeting, b: Meeting) {
        return a.meetingDateTime.getTime() - b.meetingDateTime.getTime();
    }

    public descendingMeetingsSortFunction(a: Meeting, b: Meeting) {
        return b.meetingDateTime.getTime() - a.meetingDateTime.getTime();
    }

    public getTeamMeetingsWithCustomData(teamId: number) {
        const predicate = new MethodologyPredicate<Meeting>("customData", "!=", null);

        return this.commonDataService.getWithOptions(MeetingBreezeModel, this.getTeamMeetingsRequestKey(teamId), {
            predicate,
            namedParams: {
                teamId,
            },
        });
    }

    public getEndedMeetingsForTeam(teamId: number, filters?: IMeetingFilterParams) {
        return this.getTeamMeetings(teamId, filters).pipe(
            map((meetings) => meetings
                .filter((meeting) => meeting.extensions.isEnded)
                .sort(this.descendingMeetingsSortFunction)),
        );
    }

    public getUpcomingMeetingsForTeam(teamId: number, filters?: IMeetingFilterParams) {
        return this.getTeamMeetings(teamId, filters).pipe(
            map((meetings) => meetings
                .filter((meeting) => meeting.extensions.isNotStarted)
                // earliest meeting first
                .sort(this.ascendingMeetingsSortFunction)),
        );
    }

    public getOngoingMeetingsForTeam(teamId: number, filters?: IMeetingFilterParams) {
        return this.getTeamMeetings(teamId, filters).pipe(
            map((meetings) => meetings
                .filter((meeting) => meeting.extensions.isInProgress)
                // latest meeting first - ongoing meeting has started agenda item
                .sort(this.descendingMeetingsSortFunction)),
        );
    }

    public getMeetingGuidanceForTeam(teamId: number) {
        return this.commonDataService.getById(TeamMeetingGuidanceBreezeModel, teamId);
    }

    public primeAgendaItemsForNotStartedMeeting(teamId: number) {
        const predicate = new MethodologyPredicate<MeetingAgendaItem>("meeting.teamId", "==", teamId)
            .and(new MethodologyPredicate<MeetingAgendaItem>("meeting.status", "==", MeetingStatus.NotStarted));
        const key = this.getNotStartedMeetingAgendaItemsEncompassingKey(teamId);
        return this.commonDataService.getWithOptions(MeetingAgendaItemBreezeModel, key!, {
            predicate,
            namedParams: {
                teamId,
            },
        });
    }

    public primeAttendeesForNotStartedMeeting(teamId: number) {
        const predicate = new MethodologyPredicate<MeetingAttendee>("meeting.teamId", "==", teamId)
            .and(new MethodologyPredicate<MeetingAttendee>("meeting.status", "==", MeetingStatus.NotStarted));
        const key = this.getNotStartedMeetingAttendeesEncompassingKey(teamId);
        return this.commonDataService.getWithOptions(MeetingAttendeeBreezeModel, key!, {
            predicate,
            namedParams: {
                teamId,
            },
        });
    }

    public getAgendaItemsForMeeting(meeting: Meeting, primeNotesAndItems = true, forceRemote = false) {
        const meetingId = meeting.meetingId;
        const key = `meetingAgendaItemsForMeeting${meetingId}${forceRemote}`;
        const predicate = new MethodologyPredicate<MeetingAgendaItem>("meetingId", "==", meetingId);
        const encompassingKey = meeting.extensions.isNotStarted
            ? this.getNotStartedMeetingAgendaItemsEncompassingKey(meeting.teamId)
            : undefined;

        return this.commonDataService.getWithOptions(MeetingAgendaItemBreezeModel, key, {
            forceLocal: meetingId < 0,
            forceRemote,
            predicate,
            encompassingKey,
            navProperty: "supplementaryData",
            namedParams: {
                teamId: meeting.teamId,
            },
        }).pipe(
            map((meetingAgendaItems) => meetingAgendaItems.sort((a, b) => a.ordinal - b.ordinal)),
            switchMap((agendaItems) => primeNotesAndItems
                ? forkJoin([
                    this.getMeetingNotesForMeeting(meetingId, meeting.teamId),
                    this.getLinksForMeeting(meetingId)]).pipe(
                        map(() => agendaItems),
                    )
                // don't have to prime when importing or linking items to ongoing meeting or when display meeting summary
                : of(agendaItems)),
        );
    }

    public getLinksForMeeting(meetingId: number) {
        const predicate = new MethodologyPredicate<Link>("meetingId", "==", meetingId);
        return this.commonDataService.getWithOptions(
            LinkBreezeModel,
            this.getMeetingItemsEncompassingKey(meetingId),
            {
                forceLocal: meetingId < 0,
                predicate,
                navProperty: "item,objective1",
            },
        );
    }

    public getMeetingNotesForMeeting(meetingId: number, teamId: number) {
        const predicate = new MethodologyPredicate<MeetingNote>("meetingId", "==", meetingId);
        return this.commonDataService.getWithOptions(
            MeetingNoteBreezeModel,
            this.getMeetingNotesEncompassingKey(meetingId),
            {
                forceLocal: meetingId < 0,
                predicate,
                encompassingKey: this.getMeetingNotesForMeetingsInTeamKey(teamId),
                namedParams: {
                    teamId,
                },
            },
        );
    }

    public getMeetingNotesForMeetingsInTeam(teamId: number, fromDate?: Date, toDate?: Date) {
        const predicate = new MethodologyPredicate<MeetingNote>("meeting.teamId", "==", teamId);
        const dateRangePredicate = this.getDateRangePredicate<MeetingNote>(fromDate, toDate, "meeting.");
        if (dateRangePredicate) {
            predicate.and(dateRangePredicate);
        }

        return this.commonDataService.getWithOptions(
            MeetingNoteBreezeModel,
            this.getMeetingNotesForMeetingsInTeamKey(teamId),
            {
                predicate,
                namedParams: {
                    teamId,
                },
            });
    }

    public getAgendaItemsForMeetingAgendaTemplate(meetingAgendaTemplateId: number) {
        const key = `meetingAgendaItemsForMeetingAgendaTemplate${meetingAgendaTemplateId}`;
        const predicate = new MethodologyPredicate<MeetingAgendaItem>("meetingAgendaTemplateId", "==", meetingAgendaTemplateId);

        return this.commonDataService.getWithOptions(MeetingAgendaItemBreezeModel, key, {
            // if the meeting template isn't saved yet, the items will only exist in local cache. don't bother reaching out to server.
            forceLocal: meetingAgendaTemplateId < 0,
            predicate,
            navProperty: "supplementaryData",
        }).pipe(
            map((meetingAgendaItems) => meetingAgendaItems.sort((a, b) => a.ordinal - b.ordinal)),
        );
    }

    public getMeetingAttendeesForMeeting(meeting: Meeting) {
        const meetingId = meeting.meetingId;
        const predicate = new MethodologyPredicate<MeetingAttendee>("meetingId", "==", meetingId);
        const key = `meetingAttendeesForMeeting${predicate.getKey()}`;
        const encompassingKey = meeting.extensions.isNotStarted
            ? this.getNotStartedMeetingAttendeesEncompassingKey(meeting.teamId)
            : undefined;

        return this.commonDataService.getWithOptions(MeetingAttendeeBreezeModel, key, {
            predicate,
            encompassingKey,
            namedParams: {
                teamId: meeting.teamId,
            },
        });
    }

    public primeMeetingAttendeesForMeetingsInTeam(teamId: number, fromDate?: Date, toDate?: Date) {
        const predicate = new MethodologyPredicate<MeetingAttendee>("meeting.teamId", "==", teamId);
        const dateRangePredicate = this.getDateRangePredicate<MeetingAttendee>(fromDate, toDate, "meeting.");
        if (dateRangePredicate) {
            predicate.and(dateRangePredicate);
        }

        const requestKey = MeetingAttendeeBreezeModel.identifier + (predicate?.getKey() ?? "");
        return this.commonDataService.getWithOptions(
            MeetingAttendeeBreezeModel,
            requestKey,
            {
                predicate,
                namedParams: {
                    teamId,
                },
            });
    }

    public getSupplementaryDataForMeeting(meetingId: number) {
        return this.commonDataService.getById(MeetingSupplementaryDataBreezeModel, meetingId);
    }

    public getSupplementaryDataForMeetings(meetingIds: number[]) {
        const predicate = new MethodologyPredicate<MeetingSupplementaryData>("meetingId", "in", meetingIds);

        return this.commonDataService.getByPredicate(MeetingSupplementaryDataBreezeModel, predicate);
    }

    public getMeetings(meetingIds: number[]) {
        return this.commonDataService.getByPredicate(MeetingBreezeModel, new MethodologyPredicate<Meeting>("meetingId", "in", meetingIds));
    }

    public getMeetingNotesForAgendaItem(agendaItem: MeetingAgendaItem) {
        const key = `meetingNotesForAgendaItem${agendaItem.meetingAgendaItemId}`;
        const encompassingKey = this.getMeetingNotesEncompassingKey(agendaItem.meetingId);
        const predicate = new MethodologyPredicate<MeetingNote>("meetingAgendaItemId", "==", agendaItem.meetingAgendaItemId);

        return this.commonDataService.getWithOptions(MeetingNoteBreezeModel, key, {
            // a new agenda item doesn't ever have notes
            forceLocal: agendaItem.entityAspect.entityState.isAdded(),
            encompassingKey,
            predicate,
        }).pipe(
            map((meetingNotes) => meetingNotes.sort(SortUtilities.getSortByFieldFunction<MeetingNote>("meetingNoteId"))),
        );
    }

    public createMeetingNoteForAgendaItem(agendaItem: MeetingAgendaItem, type: MeetingNoteType) {
        const initialData: Partial<MeetingNote> = {
            meetingId: agendaItem.meetingId,
            meetingAgendaItemId: agendaItem.meetingAgendaItemId,
            type,
        };

        return this.commonDataService.create(MeetingNoteBreezeModel, initialData);
    }

    public getActionLinksForAgendaItem(agendaItem: MeetingAgendaItem) {
        return this.getLinksForAgendaItem(agendaItem).pipe(
            // previously actions are always on with meetings - so this was not required. Now meetings can be run with disabled actions
            map((links) => {
                const statusAndItemCodeComparator = ItemUtilities.getItemLinkStatusAndItemCodeSortComparator();
                return links
                    .filter((meetingItem) => !!meetingItem.item?.board && this.kanbanAuthService.currentPersonCanViewBoard(meetingItem.item.board))
                    .sort(statusAndItemCodeComparator);
            }),
        );
    }

    public getObjectiveLinksForAgendaItem(agendaItem: MeetingAgendaItem) {
        return this.getLinksForAgendaItem(agendaItem).pipe(
            map((links) => links.filter((link) => !!link.objective1)),
        );
    }

    public getLinksForAgendaItem(agendaItem: MeetingAgendaItem) {
        const key = `linksForAgendaItem${agendaItem.meetingAgendaItemId}`;
        const encompassingKey = this.getMeetingItemsEncompassingKey(agendaItem.meetingId);
        const predicate = new MethodologyPredicate<Link>("meetingAgendaItemId", "==", agendaItem.meetingAgendaItemId);

        return this.commonDataService.getWithOptions(LinkBreezeModel, key, {
            // a new agenda item doesn't ever have notes
            forceLocal: agendaItem.entityAspect.entityState.isAdded(),
            predicate,
            encompassingKey,
            navProperty: "item,objective1",
        });
    }

    public getLinksForItem(itemId: number) {
        const key = `meetingItemsForItem${itemId}`;
        const predicate = new MethodologyPredicate<Link>("itemId", "==", itemId);

        return this.commonDataService.getWithOptions(LinkBreezeModel, key, {
            predicate,
            navProperty: "meeting,meetingAgendaItem",
        });
    }

    public primeLinksForMeetingsInTeam(teamId: number, fromDate?: Date, toDate?: Date) {
        const predicate = new MethodologyPredicate<Link>("meeting.teamId", "==", teamId);
        const dateRangePredicate = this.getDateRangePredicate<Link>(fromDate, toDate, "meeting.");
        if (dateRangePredicate) {
            predicate.and(dateRangePredicate);
        }

        return this.commonDataService.getByPredicate(LinkBreezeModel, predicate);
    }

    // returns empty string (falsy) if meeting startable by current person
    public getNonStartableMeetingInfo(meeting: Meeting, startWithoutAgenda = false) {
        if (meeting.extensions.isNotStarted) {
            return forkJoin([
                this.getFirstActiveMeetingForCurrentPerson(),
                this.getMeetingAttendeesForMeeting(meeting), // just to prime attendees
            ]).pipe(
                map(([activeMeeting]) => {
                    const currentPersonId = this.userService.getCurrentPersonId();
                    let meetingInfo = "";
                    if (meeting.meetingAgendaItems.length < 1 && !startWithoutAgenda) {
                        meetingInfo = "You cannot start this meeting now. There is no meeting agenda.";
                    } else if (!meeting.meetingAttendees.some((attendee) => attendee.attendeeId === currentPersonId)) {
                        meetingInfo = "You cannot start this meeting now. The meeting can only be started by one of its participants.";
                    } else if (activeMeeting) {
                        meetingInfo = "You cannot start this meeting now. You are already participating in an ongoing meeting.";
                    } else if (!this.canEditMeetingForTeam(meeting.team!)) {
                        meetingInfo = "You do not have the permission to start this meeting.";
                    }

                    return meetingInfo;
                }),
            );
        } else {
            // started or completed meeting - return empty string
            return of("");
        }
    }

    public canStartMeeting(meeting: Meeting) {
        const currentPersonId = this.userService.getCurrentPersonId();
        return forkJoin([
            this.getFirstActiveMeetingForCurrentPerson(),
            this.getMeetingAttendeesForMeeting(meeting), // just to prime attendees
        ]).pipe(
            map(([activeMeeting]) => !(activeMeeting
                || meeting.meetingAgendaItems.length < 1
                || !meeting.meetingAttendees.some((attendee) => attendee.attendeeId === currentPersonId)
                || !this.canEditMeetingForTeam(meeting.team!))),
        );
    }

    public isCurrentPersonInMeeting(meeting: Meeting) {
        const currentPersonId = this.userService.getCurrentPersonId();
        return meeting.meetingAttendees.some((attendee) => attendee.attendeeId === currentPersonId);
    }

    public getFirstActiveMeetingForCurrentPerson() {
        return this.authorisationService.getHasAccess(CommonTeamsAuthService.ViewAnyTeamMeeting).pipe(
            take(1),
            switchMap((hasAccess) => {
                if (!hasAccess) {
                    return of([]);
                }

                const currentPersonId = this.userService.getCurrentPersonId();
                if (!currentPersonId) {
                    return of([]);
                }

                const personIdPredicate = new MethodologyPredicate<MeetingAttendee>("attendeeId", "==", currentPersonId);
                const predicate = new MethodologyPredicate<Meeting>("meetingAttendees", "any", personIdPredicate)
                    .and(new MethodologyPredicate<Meeting>("status", "==", MeetingStatus.InProgress));
                return this.commonDataService.getWithOptions(MeetingBreezeModel, this.getCurrentActiveMeetingForPersonRequestKey(currentPersonId), {
                    predicate,
                    navProperty: "meetingAgendaItems,meetingAttendees,supplementaryData",
                    orderBy: "meetingDateTime ASC", // pick the older meeting first
                }).pipe(
                    map((meetings) => meetings.filter(this.filterMeetingByTeamFeatures)),
                );
            }),
            map((activeMeetingsForPerson) => {
                // There is supposed to be only a single ongoing meeting. TODO: make sure person meetings not overlapping
                return activeMeetingsForPerson.length > 0
                    ? activeMeetingsForPerson[0]
                    : undefined;
            }),
        );
    }

    private getCurrentActiveMeetingForPersonRequestKey(personId: number) {
        return `currentActiveMeetingForPerson${personId}`;
    }

    public getRecentMeetingsForPerson(personId: number, upcomingDays = 14, previousDays = 14) {
        const fromTime = moment().add(-1 * previousDays, "days");
        const toTime = moment().add(upcomingDays, "days");
        const predicate = new MethodologyPredicate<Meeting>()
            .and(new MethodologyPredicate<Meeting>("meetingDateTime", ">=", fromTime.toDate()))
            .and(new MethodologyPredicate<Meeting>("meetingDateTime", "<=", toTime.toDate()));

        return this.getMeetingsForPersonByPredicate(personId, predicate);
    }

    public getMeetingsForPersonByPredicate(personId: number, predicate: MethodologyPredicate<Meeting>) {
        const personIdPredicate = new MethodologyPredicate<MeetingAttendee>("attendeeId", "==", personId);
        const basePredicate = new MethodologyPredicate<Meeting>("meetingAttendees", "any", personIdPredicate);

        if (predicate) {
            basePredicate.and(predicate);
        }

        const key = `meetingForPersonByPredicate${personId}${basePredicate?.getKey()}`;

        return this.commonDataService.getWithOptions(MeetingBreezeModel, key, {
            predicate: basePredicate,
            navProperty: "team,meetingAgendaItems,meetingAttendees",
        }).pipe(map((meetings) => meetings.filter(this.filterMeetingByTeamFeatures)));
    }

    @Autobind
    private filterMeetingByTeamFeatures(meeting: Meeting) {
        return this.featuresService.isFeatureActive(FeatureName.StewardshipWorkMeetings, meeting.team);
    }

    public getRecentMeetingsForTeam(teamId: number, upcomingDays = 14, previousDays = 14) {
        const fromTime = moment().add(-1 * previousDays, "days");
        const toTime = moment().add(upcomingDays, "days");
        const key = `recentTeamMeetingsWithAgendaItems${teamId}`;
        const predicate = new MethodologyPredicate<Meeting>("teamId", "==", teamId)
            .and(new MethodologyPredicate<Meeting>("meetingDateTime", ">=", fromTime.toDate()))
            .and(new MethodologyPredicate<Meeting>("meetingDateTime", "<=", toTime.toDate()));

        return this.commonDataService.getWithOptions(MeetingBreezeModel, key, {
            predicate,
            navProperty: "meetingAgendaItems",
            namedParams: {
                teamId,
            },
        });
    }

    // Get all meetings the current user has access to within the last previous months
    // - don't set navProperty for this query so that entities returned are small
    public getAllRecentMeetings(previousMonths = 18, upcomingDays = 14) {
        const fromTime = moment().add(-1 * previousMonths, "months").startOf("day");    // without startOf day, it will always hit remote server
        const toTime = moment().add(upcomingDays, "days").endOf("day");                 // without startOf day, it will always hit remote server
        const predicate = new MethodologyPredicate<Meeting>("meetingDateTime", ">=", fromTime.toDate())
            .and(new MethodologyPredicate<Meeting>("meetingDateTime", "<", toTime.toDate()));
        const key = `getAllRecentMeetings_${predicate.getKey()}`;

        return this.commonDataService.getWithOptions(MeetingBreezeModel, key, {
            predicate,
            navProperty: "team",
        }).pipe(
            // latest meeting first
            map((meetings) => meetings.sort(this.descendingMeetingsSortFunction)),
        );
    }

    public getAllRecentMeetingsWithEditPermission(previousMonths = 6) {
        return this.getAllRecentMeetings(previousMonths).pipe(
            map((meetings) => meetings.filter((meeting) => this.canEditMeetingForTeam(meeting.team!))),
        );
    }

    public canEditMeetingForTeam(team: Team) {
        if (!this.authorisationService.currentPerson) {
            return false;
        }

        return this.authorisationService.personHasPermission(this.authorisationService.currentPerson, FeaturePermissionName.StewardshipWorkMeetingsEdit, team);
    }

    public canEditMeetingItemsAsParticipant(meeting: Meeting) {
        if (this.canEditMeetingForTeam(meeting.team!)) {
            return of(true);
        } else if (this.personCanParticipateInMeeting(this.authorisationService.currentPerson!, meeting)) {
            return this.personInMeeting(this.authorisationService.currentPerson!, meeting);
        } else {
            return of(false);
        }
    }

    public personCanEditMeeting(person: Person, meeting: Meeting) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.StewardshipWorkMeetingsEdit, meeting);
    }

    public personCanReadMeeting(person: Person, meeting: Meeting) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.StewardshipWorkMeetingsRead, meeting);
    }

    public personCanParticipateInMeeting(person: Person, meeting: Meeting) {
        return this.personCanEditMeeting(person, meeting) ||
            this.personCanReadMeeting(person, meeting) ||
            this.authorisationService.personHasPermission(person, FeaturePermissionName.StewardshipWorkMeetingsParticipantEdit, meeting);
    }

    public currentPersonInAnyTeamMeetings(teamId?: number) {
        // get all meetings instead of querying MeetingAttendee with meeting.teamId and attendeeId to reduce the number of queries
        // especially for organisation with plenty of teams, e.g. Carbon280 or acQuire
        // This is only 1 query for all teams instead of 40 queries for 40 teams which will choke the connections pool.
        // This will only be called by people with ParticipantEdit without MeetingRead and MeetingEdit from access verifier
        // - server will only return meetings readable
        // - without navProperty (which is agenda items and event series), the response is pretty small, just a few dates, numbers and short strings
        const query = this.commonDataService.getWithOptions(MeetingBreezeModel, AllMeetingsForPermissionVerificationRequestKey, {
            navProperty: undefined,
            // don't pass teamId query param here as we want all teams the user can access
        });

        return query.pipe(
            map((meetings) => teamId
                ? !!meetings.find((meeting) => meeting.teamId === teamId)
                : meetings.length > 0));
    }

    public personInMeeting(person: Person, meeting: Meeting) {
        const predicate = new MethodologyPredicate<MeetingAttendee>("meetingId", "==", meeting.meetingId).and(
            new MethodologyPredicate<MeetingAttendee>("attendeeId", "==", person.personId));
        return this.commonDataService.getByPredicate(MeetingAttendeeBreezeModel, predicate).pipe(map((results) => results.length > 0));
    }

    public getMeetingByIdRequestKey(meetingId: number) {
        return `get${MeetingBreezeModel.identifier}ById${meetingId}`;
    }

    public getMeetingById(meetingId: number) {
        const predicate = new MethodologyPredicate<Meeting>("meetingId", "==", meetingId);

        return this.commonDataService.getWithOptions(MeetingBreezeModel, this.getMeetingByIdRequestKey(meetingId), {
            predicate,
            navProperty: "meetingAttendees, meetingAgendaItems, supplementaryData",
        }).pipe(map(ArrayUtilities.getSingleFromArray));
    }

    public gotoTeamMeetingsPage(teamId: number, meetingId?: number) {
        return this.teamMeetingsPageRoute.gotoRoute({ teamId }, { meetingId }).pipe(
            // as reloadOnSearch is false for meeting page, ngOnInit() won't be called if already in the page
            // - this is to notify the page that the meeting id is changed
            tap(() => {
                if (meetingId) {
                    this.notifyMeetingIdChange(meetingId);
                }
            }),
        );
    }

    public getTeamMeetingsPageObjForTeam(teamId: number) {
        return this.teamMeetingsPageRoute.getRouteObject({ teamId });
    }

    public getTeamMeetingsPageForTeam(teamId: number) {
        return this.teamMeetingsPageRoute.getRoute({ teamId });
    }

    public getTeamMeetingsPageObj(meeting: Meeting) {
        const fromDate = this.getMeetingFromDateFilterParamIfNecessary(meeting);
        return this.teamMeetingsPageRoute.getRouteObject({ teamId: meeting.teamId }, { meetingId: meeting.meetingId, fromDate });
    }

    public getTeamMeetingsPage(meeting: Meeting) {
        const fromDate = this.getMeetingFromDateFilterParamIfNecessary(meeting);
        return this.teamMeetingsPageRoute.getRoute({ teamId: meeting.teamId }, { meetingId: meeting.meetingId, fromDate });
    }

    private getMeetingFromDateFilterParamIfNecessary(meeting: Meeting) {
        return (moment(DefaultMeetingFilter.fromDate).isAfter(meeting.endTime))
            // the default filter will result in meeting not showing - need to change the fromDate filter to before meeting start time
            ? moment(meeting.meetingDateTime).subtract(1, "day").startOf("day").toISOString()
            : undefined;
    }

    @Autobind
    public gotoMeetingPage(meeting: Meeting) {
        return this.gotoTeamMeetingsPage(meeting.teamId, meeting.meetingId);
    }

    @Autobind
    public gotoActiveMeetingPage(meeting: Meeting, returnWorkflowPath?: string, showDescription: boolean = false) {
        return teamActiveMeetingPageRoute.gotoRoute({
            teamId: meeting.teamId,
            meetingId: meeting.meetingId,
        }, { returnWorkflowPath, showDescription }).pipe(
            // as reloadOnSearch is false for meeting page, ngOnInit() won't be called if already in the page
            // - this is to notify the page that the meeting id is changed
            tap(() => this.notifyMeetingIdChange(meeting.meetingId)),
        );
    }

    public groupMeetingsByTeam(meetings: Meeting[]): IMeetingTeamGroup[] {
        const meetingsByTeam = ArrayUtilities.groupArrayBy(meetings, (o) => o.team);
        return meetingsByTeam
            .map((group) => ({
                team: group.key,
                meetings: group.items.sort(this.ascendingMeetingsSortFunction),
            }))
            .sort((g1, g2) => (g1.team?.name ?? "").localeCompare(g2.team?.name ?? ""));
    }

    public getTotalMeetingsCountInTeam(teamId: number) {
        const predicate = new MethodologyPredicate<Meeting>("teamId", "==", teamId);
        return this.commonDataService.getCountByPredicate(MeetingBreezeModel, predicate);
    }

    private getTeamMeetings(teamId: number, filters?: IMeetingFilterParams) {
        // fromDate is special - if filters is undefined - default to DefaultMeetingFilter.fromDate
        const fromDate = filters ? filters.fromDate : DefaultMeetingFilter.fromDate;
        const previousDateRangeKey = this.cacheDateRangeKey[teamId];
        const dateRangeKey = this.getDateRangeKey(fromDate, filters?.toDate);
        if (!isEqual(dateRangeKey, previousDateRangeKey)) {
            this.commonDataService.clearQueryCacheForRequestKey(this.getTeamMeetingsRequestKey(teamId));
            if (filters?.showDecisions || filters?.showMinutes) {
                this.commonDataService.clearQueryCacheForRequestKey(this.getMeetingNotesForMeetingsInTeamKey(teamId));
            }

            this.cacheDateRangeKey[teamId] = dateRangeKey; // update the cache key
        }

        const predicate = new MethodologyPredicate<Meeting>("teamId", "==", teamId);
        const dateRangePredicate = this.getDateRangePredicate<Meeting>(fromDate, filters?.toDate);
        if (dateRangePredicate) {
            predicate.and(dateRangePredicate);
        }

        return this.commonDataService.getWithOptions(MeetingBreezeModel, this.getTeamMeetingsRequestKey(teamId), {
            predicate,
            navProperty: "meetingAgendaItems,eventSeries.eventType",
            namedParams: {
                teamId,
            },
        }).pipe(
            switchMap((meetings) => {
                if (!filters || meetings.length < 1 ||
                    (!filters.showActions && !filters.showObjectives && !filters.showDecisions && !filters.showMinutes && !filters.peopleIds?.length)) {
                    // all unticked -> no further filtering
                    return of(meetings);
                }

                const primeActions: Observable<unknown>[] = [];
                if (filters.peopleIds?.length) {
                    primeActions.push(this.primeMeetingAttendeesForMeetingsInTeam(teamId, fromDate, filters?.toDate));
                }

                if (filters.showActions || filters.showObjectives) {
                    primeActions.push(this.primeLinksForMeetingsInTeam(teamId, fromDate, filters?.toDate));
                }

                if (primeActions.length < 1) {
                    primeActions.push(of(null)); // just to make sure we have something to forkJoin
                }

                // primeActions cannot be empty - already guarded by the return above
                return forkJoin(primeActions).pipe(
                    switchMap(() => filters.showDecisions || filters.showMinutes
                        ? this.getMeetingNotesForMeetingsInTeam(teamId, fromDate, filters?.toDate)
                        : of([])),
                    map((meetingNotes) => meetings.filter((meeting) =>
                        (!filters?.peopleIds?.length || meeting.meetingAttendees.some((attendee) => filters.peopleIds.includes(attendee.attendeeId))) &&
                        (!(filters.showActions || filters.showObjectives || filters.showDecisions || filters.showMinutes) ||
                            meeting.links.some((link) =>
                                (filters.showActions && link.itemId) || (filters.showObjectives && link.objective1Id)) ||
                            (filters.showDecisions && meetingNotes.some(
                                (note) => note.meetingId === meeting.meetingId && note.type === MeetingNoteType.Decision)) ||
                            (filters.showMinutes && meetingNotes.some(
                                (note) => note.meetingId === meeting.meetingId && note.type === MeetingNoteType.Minute)))
                    )),
                );
            }),
        );
    }

    private getDateRangeKey(fromDate?: Date, toDate?: Date) {
        const fromDateKey = fromDate ? moment(fromDate).format("YYYYMMDD") : undefined;
        const toDateKey = toDate ? moment(toDate).format("YYYYMMDD") : undefined;
        return fromDateKey || toDateKey
            ? `dateRange_${fromDateKey}_${toDateKey}`
            : undefined;
    }

    private getDateRangePredicate<T extends IBreezeEntity<T>>(fromDate?: Date, toDate?: Date, prefix = "") {
        const roundedFromDate = fromDate ? moment(fromDate).startOf("day") : undefined;
        const roundedToDate = toDate ? moment(toDate).endOf("day") : undefined;
        if (!roundedFromDate && !roundedToDate) {
            return undefined;
        }

        let predicate: MethodologyPredicate<T> | undefined;
        if (roundedFromDate) {
            predicate = new MethodologyPredicate<T>(`${prefix}endTime` as any, ">=", roundedFromDate.toDate());
        }

        if (roundedToDate) {
            const startTimePredicate = new MethodologyPredicate<T>(`${prefix}meetingDateTime` as any, "<=", roundedToDate.toDate());
            if (predicate) {
                predicate = predicate.and(startTimePredicate);
            } else {
                predicate = startTimePredicate;
            }
        }

        return predicate;
    }

    private getTeamMeetingsRequestKey(teamId: number) {
        return `teamMeetingsWithAgendaItems${teamId}`;
    }

    private getMeetingNotesEncompassingKey(meetingId: number) {
        return `meetingNotesForMeeting${meetingId}`;
    }

    private getMeetingNotesForMeetingsInTeamKey(teamId: number) {
        return `meetingNotesForMeetingsInTeam${teamId}`;
    }

    private getMeetingItemsEncompassingKey(meetingId: number) {
        return `linksForMeeting${meetingId}`;
    }

    private getNotStartedMeetingAgendaItemsEncompassingKey(teamId: number) {
        return `meetingAgendaItemsForNotStartedMeetingForTeam${teamId}`;
    }

    private getNotStartedMeetingAttendeesEncompassingKey(teamId: number) {
        return `meetingAttendeesForNotStartedMeetingForTeam${teamId}`;
    }
}
